import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { InstagramLogo, FacebookLogo, YoutubeLogo, TwitterLogo, WhatsappLogo } from '@phosphor-icons/react';
import { publicApi } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_groove-tours/artifacts/bqmuqiqz_IMG-20260208-WA0097.jpg";

export const Footer = () => {
  const [settings, setSettings] = useState(null);
  const { t } = useLanguage();

  useEffect(() => {
    publicApi.getSettings().then(res => setSettings(res.data)).catch(() => {});
  }, []);

  const contact = settings?.contact || {};
  const social = settings?.social_links || {};

  const socialLinks = [
    { icon: InstagramLogo, url: social.instagram, label: 'Instagram' },
    { icon: FacebookLogo, url: social.facebook, label: 'Facebook' },
    { icon: YoutubeLogo, url: social.youtube, label: 'YouTube' },
    { icon: TwitterLogo, url: social.twitter, label: 'Twitter' },
  ].filter(s => s.url);

  return (
    <footer className="bg-[#012A36] text-white noise-overlay" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src={LOGO_URL} 
                alt="Caribbean Groove Tours" 
                className="w-16 h-16 rounded-2xl object-cover"
              />
              <span className="font-semibold text-xl">Caribbean Groove</span>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              {t('footer_description')}
            </p>
            {socialLinks.length > 0 && (
              <div className="flex gap-3">
                {socialLinks.map((s, i) => (
                  <a
                    key={i}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center hover:bg-[#FF6B4A] transition-colors duration-300"
                    aria-label={s.label}
                  >
                    <s.icon weight="fill" className="w-5 h-5" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6 text-[#00A896]">{t('nav_inicio')}</h4>
            <ul className="space-y-3">
              {[
                { to: '/', label: t('nav_inicio') },
                { to: '/excursiones', label: t('nav_excursiones') },
                { to: '/reservar', label: t('nav_reservar') },
                { to: '/contacto', label: t('nav_contacto') },
                { to: '/admin', label: t('nav_admin') },
              ].map((link, i) => (
                <li key={i}>
                  <Link 
                    to={link.to} 
                    className="text-white/60 hover:text-white hover:translate-x-1 transition-all inline-block"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Tours */}
          <div>
            <h4 className="font-semibold text-lg mb-6 text-[#00A896]">{t('footer_tours')}</h4>
            <ul className="space-y-3">
              {[
                { to: '/tour/isla-saona', label: 'Isla Saona' },
                { to: '/tour/cayo-arena', label: 'Cayo Arena' },
                { to: '/tour/samana-salto-limon', label: 'Samaná + Salto El Limón' },
                { to: '/tour/montana-redonda-miches', label: 'Montaña Redonda' },
              ].map((link, i) => (
                <li key={i}>
                  <Link 
                    to={link.to} 
                    className="text-white/60 hover:text-white hover:translate-x-1 transition-all inline-block"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-lg mb-6 text-[#00A896]">{t('footer_contact')}</h4>
            <ul className="space-y-4">
              {contact.whatsapp && (
                <li>
                  <a 
                    href={`https://wa.me/${contact.whatsapp?.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-white/60 hover:text-[#25D366] transition-colors"
                  >
                    <WhatsappLogo weight="fill" className="w-5 h-5" />
                    {contact.whatsapp}
                  </a>
                </li>
              )}
              {contact.email && (
                <li>
                  <a 
                    href={`mailto:${contact.email}`}
                    className="flex items-center gap-3 text-white/60 hover:text-[#FF6B4A] transition-colors"
                  >
                    <Mail className="w-5 h-5" />
                    {contact.email}
                  </a>
                </li>
              )}
              {contact.address && (
                <li className="flex items-start gap-3 text-white/60">
                  <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{contact.address}</span>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Caribbean Groove Tours. {t('footer_rights')}.
          </p>
          <p className="text-white/40 text-sm">
            {t('footer_created')} <span className="text-[#FF6B4A] font-medium">_impulso365</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
